import { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./clinicalDiagram.module.css";
import {
  useGetClinicalDiagramDetailQuery,
  useUpdateClinicalDiagramDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";
import { useResolvedBlobUrl } from "./useResolvedBlobUrl";

/**
 * Renders the Clinical Diagram details view.
 *
 * The view fetches diagram details, resolves pre-signed URLs to blob URLs
 * for display, and shows deactivation metadata when applicable.
 *
 * Returns:
 *   JSX.Element: The Clinical Diagram Details page content.
 */
const ClinicalDiagramDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: clinicalDiagramDetails,
    isLoading,
    isFetching,
  } = useGetClinicalDiagramDetailQuery(id ?? "");
  const [updateClinicalDiagramDetails] =
    useUpdateClinicalDiagramDetailsMutation();

  // Resolve pre-signed URLs into blob URLs and revoke old ones to avoid memory leaks.
  const diagramSrc = useResolvedBlobUrl(clinicalDiagramDetails?.diagram_pre_signed_url);
  const thumbnailSrc = useResolvedBlobUrl(clinicalDiagramDetails?.thumbnail_pre_signed_url);

  const handleActivate = async () => {
    if (!clinicalDiagramDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateClinicalDiagramDetails({
        is_active: true,
        id: clinicalDiagramDetails.id,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };


  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.clinicalDiagramDetailHeading}>
              Clinical Diagram Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {clinicalDiagramDetails?.name}
                </Typography>
              </Box>
            </Box>
            <Box className={style.row} sx={{ flexDirection: "column", marginTop: "20px" }}>
              <Typography
                className={style.inputLabel}
                sx={{ marginBottom: -2, display: "flex" }}
              >
                Thumbnail
              </Typography>
              <Box sx={{ width: "100%", display: 'flex', justifyContent: 'center' }}>
                <img
                  src={thumbnailSrc}
                  alt={`${clinicalDiagramDetails?.name ?? "Clinical diagram"} thumbnail`}
                  style={{
                    padding: 5,
                    minHeight: "250px",
                    maxHeight: "250px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    overflow: "hidden",
                    borderRadius: 6,
                  }}
                />
              </Box>
            </Box>
            <Box className={style.row} sx={{ flexDirection: "column", marginTop: "5px" }}>
              <Typography
                className={style.inputLabel}
                sx={{ marginBottom: -2, display: "flex" }}
              >
                Diagram
              </Typography>
              <Box sx={{ width: "100%", display: 'flex', justifyContent: 'center' }}>
                <img
                  key={diagramSrc}
                  src={diagramSrc}
                  alt={`${clinicalDiagramDetails?.name ?? "Clinical diagram"} diagram`}
                  style={{
                    padding: 5,
                    minHeight: "250px",
                    maxHeight: "250px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    overflow: "hidden",
                    borderRadius: 6,
                  }}
                />
              </Box>
            </Box>
            {clinicalDiagramDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.clinicalDiagramDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {clinicalDiagramDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {clinicalDiagramDetails?.deactivated_at
                        ? dayjs(clinicalDiagramDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {clinicalDiagramDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(`/clinical-diagrams/edit/${clinicalDiagramDetails?.id}`)
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${clinicalDiagramDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box >
  );
};

export default ClinicalDiagramDetails;
